<template>
    <div class="details-body">
        <b-form-group
            label-for="room"
            label-cols="3"
            label="Room:"
            class="mb-0"
        >
            <b-form-input plaintext :value="roomLabel"></b-form-input>
        </b-form-group>
        <b-form-group
            label-for="checkin"
            label-cols="3"
            label="Check In:"
            class="mb-0"
        >
            <b-form-input plaintext :value="checkInDate"></b-form-input>
        </b-form-group>
        <b-form-group
            label-for="checkout"
            label-cols="3"
            label="Check Out:"
            class="mb-0"
        >
            <b-form-input plaintext :value="checkOutDate"></b-form-input>
        </b-form-group>
        <b-form-group
            label-for="provider"
            label-cols="3"
            label="Provider:"
            class="mb-0 custom-flex"
        >
            <div
                v-if="isLoadingExtraDetails"
                class="loading-skeleton"
                style="width: 130px;"
            ></div>
            <span v-else>{{ provider }}</span>
        </b-form-group>
        <b-form-group
            label-for="client"
            label-cols="3"
            label="Client:"
            class="mb-0 custom-flex"
        >
            <div
                v-if="isLoadingExtraDetails"
                class="loading-skeleton"
                style="width: 120px;"
            ></div>
            <span v-else>
                <router-link :to="client.path">
                    {{ client.name }}
                </router-link>
                <span v-if="client.mobile">(M: {{ client.mobile }})</span>
            </span>
        </b-form-group>
        <b-form-group
            label-for="guest"
            label-cols="3"
            label="Guest:"
            class="mb-0 custom-flex"
        >
            <div
                v-if="isLoadingExtraDetails"
                class="loading-skeleton"
                style="width: 100px;"
            ></div>
            <router-link v-else :to="patient.path">
                {{ patient.name }}
            </router-link>
        </b-form-group>
        <b-form-group
            v-if="hasCheckedInOut"
            label-for="medical-note"
            label-cols="3"
            label="Medical Note:"
            class="mb-0 custom-flex"
        >
            <div
                v-if="isLoadingExtraDetails"
                class="loading-skeleton"
                style="width: 130px;"
            ></div>
            <a v-else-if="medicalNote.name !== ''" :href="medicalNote.link">
                {{ medicalNote.name }}
            </a>
            <span v-else>--</span>
        </b-form-group>
        <b-form-group
            v-if="hasCheckedInOut"
            label-for="form"
            label-cols="3"
            label="Form:"
            class="mb-0 custom-flex"
        >
            <div
                v-if="isLoadingExtraDetails"
                class="loading-skeleton"
                style="width: 130px;"
            ></div>
            <a v-else-if="letterForm.name !== ''" :href="letterForm.link">
                {{ letterForm.name }}
            </a>
            <span v-else>--</span>
        </b-form-group>
        <b-form-group
            v-if="hasCheckedInOut"
            label-for="invoice"
            label-cols="3"
            label="Invoice:"
            class="mb-0 custom-flex"
        >
            <div
                v-if="isLoadingExtraDetails"
                class="loading-skeleton"
                style="width: 130px;"
            ></div>
            <router-link v-else-if="invoice.name !== ''" :to="invoice.link">
                {{ invoice.name }}
            </router-link>
            <span v-else>--</span>
        </b-form-group>
        <b-form-group
            label-for="notes"
            label-cols="3"
            label="Notes:"
            class="mb-0 custom-flex"
        >
            <div
                v-if="isLoadingExtraDetails"
                class="loading-skeleton"
                style="width: 150px;"
            ></div>
            <span v-else :class="{ 'mt-2': isNoteMultipleLine, note: true }">
                {{ note }}
            </span>
        </b-form-group>
    </div>
</template>

<script>
import BoardingService from "@/services/boarding.js";

export default {
    props: {
        details: {
            type: Object
        },
        providers: {
            type: Array
        }
    },
    data() {
        return {
            extraDetails: null,
            isLoadingExtraDetails: true,
            sessionStorageKeys: [],
            ssKey: "boarding_booking_details_" + this.details.id,
            checkInDate: "-",
            checkOutDate: "-"
        };
    },
    computed: {
        roomLabel() {
            return this.details?.record?.room?.name ?? "--";
        },
        hasCheckedInOut() {
            if (this.extraDetails) {
                // 1 - checked in
                // 2 - checked out
                return [1, 2].includes(this.extraDetails.status);
            }
            return false;
        },
        client() {
            let client = { name: "--", path: "#" };
            if (this.extraDetails && this.extraDetails.client) {
                let {
                    label,
                    encoded_id: path,
                    mobile
                } = this.extraDetails.client;
                client.name = label;
                client.path = `/client/profile/${path}`;
                client.mobile = mobile || null;
            }
            return client;
        },
        patient() {
            let patient = { name: "--", path: "#" };
            if (this.extraDetails && this.extraDetails.patient) {
                let { name, encoded_id: path } = this.extraDetails.patient;
                patient.name = name;
                patient.path = `/patient/profile/${path}`;
            }
            return patient;
        },
        note() {
            if (this.extraDetails.note !== "") {
                return this.extraDetails.note;
            }
            return "--";
        },
        isNoteMultipleLine() {
            let str = this.extraDetails.note || "";
            if (/\r|\n/.exec(str)) {
                return true;
            }
            return false;
        },
        provider() {
            let sources = this.providers || [];
            let staff = sources.find(
                src => src.actor_id === this.extraDetails.staff_id
            );

            if (staff && staff.actor) {
                return staff.actor.label || staff.actor.display_name || "--";
            }

            return "--";
        },
        medicalNote() {
            let details = this.extraDetails;
            let name = "";
            let link = "#";

            name = details?.soap?.template?.name ?? "";
            link = details?.soap?.ci_url ?? "#";

            if (link !== "#") {
                link = "/apps/index.php/" + link;
            }

            return {
                name,
                link
            };
        },
        letterForm() {
            let details = this.extraDetails;
            let name = "";
            let link = "#";

            name = details?.letter?.title ?? "";
            link = details?.letter?.ci_url ?? "#";

            if (link !== "#") {
                link = "/apps/index.php/" + link;
            }

            return {
                name,
                link
            };
        },
        invoice() {
            let details = this.extraDetails;
            let name = "";
            let link = "#";

            name = details?.invoice?.name ?? "";
            link = details?.invoice?.encoded_id ?? "#";

            if (link !== "#") {
                link = "/billing/invoice/" + link;
            }

            return {
                name,
                link
            };
        },
        isLoadingFooterPreview: {
            get() {
                return this.$store.state.modalReservation.isLoadingFooterPreview; // prettier-ignore
            },
            set(value) {
                this.$store.commit(
                    "modalReservation/setIsLoadingDetails",
                    value
                );
            }
        }
    },
    mounted() {
        let stored = sessionStorage.getItem(this.ssKey);

        if (stored) {
            let details = JSON.parse(stored);
            this.isLoadingExtraDetails = false;
            this.extraDetails = details;
            this.$emit("details-loaded", details);
            this.fetchDetails(true);
        } else {
            this.fetchDetails();
        }

        this.applyDateFormat();
    },
    methods: {
        applyDateFormat() {
            const format = this.getDateFormat() + ", hh:mm A";
            const formatter = (date) => moment(date).tz(this._timeZone).format(format); // prettier-ignore
            this.checkInDate = formatter(this.details.start);
            this.checkOutDate = formatter(this.details.end);
        },
        async fetchDetails(skipLoader = false) {
            try {
                if (!skipLoader) {
                    this.isLoadingExtraDetails = true;
                }
                this.isLoadingFooterPreview = true;

                let res = await BoardingService.getBoardingById(this.details.id); // prettier-ignore

                this.isLoadingExtraDetails = false;
                this.isLoadingFooterPreview = false;

                this.extraDetails = res;
                this.$emit("details-loaded", res);
                sessionStorage.setItem(this.ssKey, JSON.stringify(res)); // session storage register
            } catch (error) {
                this.isLoadingExtraDetails = false;
                this.extraDetails = null;
                console.log(error);
            }
        }
    }
};
</script>

<style scoped>
.details-body {
    padding: 10px 20px;
}
::v-deep .custom-flex .bv-no-focus-ring {
    display: flex;
    align-items: center;
}
.loading-skeleton {
    background: #f6f7f8;
    background: linear-gradient(to right, #f6f6f6 8%, #eeeeee 30%, #f6f6f6 33%);
    width: 100px;
    height: 16px;
    animation-duration: 10s;
    animation-fill-mode: forwards;
    animation-iteration-count: infinite;
    animation-name: loading;
    animation-timing-function: linear;
}
.note {
    white-space: pre-line;
}
</style>
